import { useState, useCallback, useRef } from 'react';
import { searchPersons } from '../../services/api/personService';

/**
 * Custom hook for searching people with characteristics and filters
 * Migrated from GraphQL to REST API (QueryPersons)
 * @param {Function} setFilteredPeople - Function to update filtered people list
 * @param {Function} setMatchSource - Function to update match source
 * @returns {Object} Search functions and state
 */
export function useSearchPeople(setFilteredPeople, setMatchSource) {
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Use ref instead of module-level variable to prevent shared state between hook instances
  const peopleFromAPIRef = useRef([]);

  const searchPeople = useCallback(async (map, searchString, selectedDatasets = []) => {
    if (map === undefined) {
      return;
    }

    // Check if map has no characteristics (excluding Dataset which is now handled separately)
    const hasNoCharacteristics = map.size === 0;

    // Allow search if we have a search string or selected datasets, otherwise prevent
    if (hasNoCharacteristics && (!searchString || searchString.trim() === '') && selectedDatasets.length === 0) {
      setSnackbarMessage('Please select at least one characteristic, dataset, or enter a search term');
      setSnackbarOpen(true);
      setFilteredPeople(new Array(0));
      setMatchSource('manual_filter');
      return;
    }

    // Build base search parameters from map and searchString
    const baseSearchParams = {};

    // Convert Map to search parameters (excluding Dataset - now passed as selectedDatasets array)
    map.forEach((value, key) => {
      if (value && value !== '' && key !== 'Dataset') {
        // Convert key to lowercase to match API parameter names
        const paramKey = key.toLowerCase();
        baseSearchParams[paramKey] = value;
      }
    });

    // Add text search if provided
    if (searchString && searchString.trim() !== '') {
      baseSearchParams.search = searchString.trim();
    }

    // If no datasets selected, search all datasets (don't pass dataset parameter)
    // If datasets selected, search across all selected datasets
    try {
      let allPersons = [];

      if (selectedDatasets.length === 0) {
        // No dataset filter - search across all
        console.log('Searching persons across all datasets with parameters:', baseSearchParams);
        const persons = await searchPersons(baseSearchParams);
        allPersons = persons;
      } else {
        // Search each selected dataset and combine results
        console.log(`Searching persons across ${selectedDatasets.length} datasets:`, selectedDatasets);
        const searchPromises = selectedDatasets.map(datasetId => {
          const paramsWithDataset = { ...baseSearchParams, dataset: datasetId };
          return searchPersons(paramsWithDataset);
        });

        const results = await Promise.all(searchPromises);

        // Combine and deduplicate results by person_id
        const personsMap = new Map();
        results.forEach(persons => {
          persons.forEach(person => {
            if (!personsMap.has(person.person_id)) {
              personsMap.set(person.person_id, person);
            }
          });
        });

        allPersons = Array.from(personsMap.values());
      }

      peopleFromAPIRef.current = allPersons;

      if (allPersons.length > 50) {
        setConfirmOpen(true);
      } else {
        setFilteredPeople(allPersons);
        setMatchSource('manual_filter');
      }
    } catch (error) {
      console.error('Error searching persons:', error);
      setSnackbarMessage(`Search failed: ${error.message}`);
      setSnackbarOpen(true);
      setFilteredPeople(new Array(0));
      setMatchSource('manual_filter');
    }
  }, [setFilteredPeople, setMatchSource]);

  const pullEntireSet = useCallback(() => {
    setFilteredPeople(peopleFromAPIRef.current);
    setMatchSource('manual_filter');
  }, [setFilteredPeople, setMatchSource]);

  const clearResults = useCallback((defaultMap, setEnumMap) => {
    setFilteredPeople(new Array(0));
    setMatchSource('manual_filter');
    setEnumMap(defaultMap);
  }, [setFilteredPeople, setMatchSource]);

  return {
    searchPeople,
    pullEntireSet,
    clearResults,
    peopleFromAPI: peopleFromAPIRef.current,
    snackbarMessage,
    snackbarOpen,
    setSnackbarOpen,
    confirmOpen,
    setConfirmOpen
  };
}
